import React, { useEffect, useState, useCallback, Fragment } from "react";
import "echarts-gl";
import { Radio } from "antd";
import ReactEcharts from "echarts-for-react";
import { useSelector, useDispatch } from "react-redux";
import { map } from "lodash-es";
import { useTranslation } from "react-i18next";

import {
  selectYear,
  selectMonth,
  selectDay,
  selectCategory,
  selectShipType,
  selectCurrentLayerGroup,
} from "@/selector";
import {
  getDefaultYear,
  getDefaultMonth,
  getDefaultDay,
  promiseDispatch,
} from "@/util";
import HistoryRanges from "@/util/history_range";
import ForecastRanges from "@/util/forecast_range";
import IrregularRanges from "@/util/irregular_range";
import { actionCreators } from "@/duck/dashboard";

const options = [
  { label: "密集度", value: "concentration" },
  { label: "厚度", value: "thickness" },
  { label: "风险值", value: "rv" },
];

const enOptions = [
  { label: "Concentration", value: "concentration" },
  { label: "Thickness", value: "thickness" },
  { label: "RIO", value: "rv" },
];

const COLOR_MAP = {
  thickness: [
    "#0000FF",
    "#0033FF",
    "#0066FF",
    "#0099FF",
    "#00CCFF",
    "#00FFFF",
    "#00FFCC",
    "#00FF99",
    "#00FF66",
    "#00FF33",
    "#00FF00",
    "#33FF00",
    "#66FF00",
    "#99FF00",
    "#CCFF00",
    "#FFFF00",
    "#FFCC00",
    "#FF9900",
    "#FF6600",
    "#FF3300",
    "#FF0000",
  ],
  concentration: [
    "#0000FF",
    "#0033FF",
    "#0066FF",
    "#0099FF",
    "#00CCFF",
    "#00FFFF",
    "#00FFCC",
    "#00FF99",
    "#00FF66",
    "#00FF33",
    "#00FF00",
    "#33FF00",
    "#66FF00",
    "#99FF00",
    "#CCFF00",
    "#FFFF00",
    "#FFCC00",
    "#FF9900",
    "#FF6600",
    "#FF3300",
    "#FF0000",
  ],
  rv: [
    "#FF0000",
    "#FF3300",
    "#FF6600",
    "#FF9900",
    "#FFCC00",
    "#FFFF00",
    "#CCFF00",
    "#99FF00",
    "#66FF00",
    "#33FF00",
    "#00FF00",
    "#00FF33",
    "#00FF66",
    "#00FF99",
    "#00FFCC",
    "#00FFFF",
    "#00CCFF",
    "#0099FF",
    "#0066FF",
    "#0033FF",
    "#0000FF",
  ],
};

const IceSituation = () => {
  const { i18n, t } = useTranslation();
  const { innerWidth } = window;
  const fontSize = innerWidth > 4000 ? 16 : 12;
  const size = innerWidth > 4000 ? "large" : "small";
  const [type, setType] = useState("concentration");
  const [data, setData] = useState([]);
  const dispatch = useDispatch();
  const currentLayerGroup = useSelector(selectCurrentLayerGroup);
  const category = useSelector(selectCategory);
  const year = useSelector(selectYear) || getDefaultYear(category);
  const month = useSelector(selectMonth) || getDefaultMonth(category);
  const day = useSelector(selectDay) || getDefaultDay();
  const shipType = useSelector(selectShipType);
  let ranges = HistoryRanges?.[year]?.[month] || [];
  if (category === "irregular") {
    ranges = IrregularRanges?.[year]?.[month] || [];
  } else if (category === "forecast") {
    ranges = ForecastRanges?.[year]?.[month] || [];
  }
  let points = [];
  const result = map(data, (item) => [item.lon, item.lat, item[type]]);
  if (result.length > 0) {
    points = [
      [...result[0], t("ice.start")],
      [...result[result.length - 1], t("ice.end")],
    ];
  }
  useEffect(() => {
    const labelMonth = month < 10 ? `0${month}` : `${month}`;
    const labelDay = day < 10 ? `0${day}` : `${day}`;
    let layerName = "";
    if (category === "history") {
      layerName = `history/${shipType}_passage_${year}-${labelMonth}-${labelDay}`;
    } else if (category === "forecast") {
      layerName = `forecast/${shipType}_future_passage_${year}-${labelMonth}-${labelDay}`;
    } else {
      layerName = `irregular/irregular_${shipType}_passage_${year}-${labelMonth}-${labelDay}`;
    }
    const passageDate = `${year}-${month}-${day}`;
    promiseDispatch(dispatch, actionCreators.dashboard.panel.query.situation, {
      layerName,
      passageDate,
    }).then((response) => {
      if (response) {
        setData(response);
      }
    });
  }, [dispatch, category, year, month, day, shipType, setData]);

  const onTypeChange = useCallback((e) => setType(e.target.value), [setType]);

  return (
    <div
      style={{
        position: "relative",
        height: "100%",
        width: "100%",
      }}
    >
      {currentLayerGroup === "route" && ranges.includes(day) ? (
        <Fragment>
          <div
            style={{
              position: "absolute",
              top: "2px",
              left: "2px",
              zIndex: 999,
            }}
          >
            <Radio.Group
              options={i18n.language === "zh" ? options : enOptions}
              onChange={onTypeChange}
              value={type}
              size={size}
              optionType="button"
              buttonStyle="solid"
            />
          </div>
          <div style={{ height: "100%", width: "100%" }}>
            <ReactEcharts
              option={{
                tooltip: {},
                legend: {
                  show: false,
                },
                visualMap: {
                  show: false,
                  dimension: 2,
                  min: 0,
                  max: 30,
                  inRange: {
                    color: COLOR_MAP[type],
                  },
                },
                xAxis3D: {
                  type: "value",
                  name: "",
                  axisLabel: {
                    color: "rgba(255,255,255,0.75)",
                    fontSize: fontSize,
                    lineHeight: 16,
                    end: true,
                    margin: innerWidth > 4000 ? 24 : 12,
                  },
                  axisLine: {
                    show: true,
                    lineStyle: {
                      color: "rgba(255, 255, 255, 0.75)",
                    },
                  },
                  axisTick: {
                    show: true,
                    lineStyle: {
                      color: "rgba(255, 255, 255, 0.75)",
                    },
                  },
                  splitLine: {
                    show: true,
                    lineStyle: {
                      color: "rgba(255, 255, 255, 0.35)",
                    },
                  },
                },
                yAxis3D: {
                  type: "value",
                  name: "",
                  min: 30,
                  max: 90,
                  axisLabel: {
                    color: "rgba(255,255,255,0.75)",
                    fontSize: fontSize,
                    lineHeight: 16,
                    end: true,
                    margin: innerWidth > 4000 ? 24 : 12,
                  },
                  axisLine: {
                    show: true,
                    lineStyle: {
                      color: "rgba(255, 255, 255, 0.75)",
                    },
                  },
                  axisTick: {
                    show: true,
                    lineStyle: {
                      color: "rgba(255, 255, 255, 0.75)",
                    },
                  },
                  splitLine: {
                    show: true,
                    lineStyle: {
                      color: "rgba(255, 255, 255, 0.35)",
                    },
                  },
                },
                zAxis3D: {
                  type: "value",
                  name: "",
                  axisLabel: {
                    color: "rgba(255,255,255,0.75)",
                    fontSize: fontSize,
                    lineHeight: 16,
                    end: true,
                    margin: innerWidth > 4000 ? 30 : 20,
                  },
                  axisLine: {
                    show: true,
                    lineStyle: {
                      color: "rgba(255, 255, 255, 0.75)",
                    },
                  },
                  axisTick: {
                    show: true,
                    lineStyle: {
                      color: "rgba(255, 255, 255, 0.75)",
                    },
                  },
                  splitLine: {
                    show: true,
                    lineStyle: {
                      color: "rgba(255, 255, 255, 0.35)",
                    },
                  },
                },
                grid3D: {
                  viewControl: {
                    projection: "orthographic",
                  },
                  boxWidth: innerWidth > 4000 ? 100 : 80,
                  boxDepth: innerWidth > 4000 ? 100 : 80,
                  boxHeight: innerWidth > 4000 ? 60 : 60,
                  top: 5,
                  right: 5,
                  left: 5,
                  bottom: 5,
                },
                series: [
                  {
                    type: "line3D",
                    name: t(`ice.${type}`),
                    data: result,
                    lineStyle: { width: 5 },
                  },
                  {
                    type: "scatter3D",
                    name: "Point",
                    itemStyle: {
                      color: "rgb(116,173,209)",
                    },
                    label: {
                      show: true,
                      position: "top",
                      distance: 1,
                      textStyle: {
                        color: "rgb(255,255, 255, 0.75)",
                        fontSize: 14,
                        borderWidth: 0,
                        borderColor: "#313695",
                        backgroundColor: "transparent",
                      },
                    },
                    data: points,
                  },
                ],
              }}
              style={{ height: "100%", width: "100%" }}
            />
          </div>
        </Fragment>
      ) : (
        t("ice.nodata")
      )}
    </div>
  );
};

export default IceSituation;
